import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import BackToCockpit from "@/components/BackToCockpit";
import { FileText, Upload, Trash2, CheckCircle2, Loader2, AlertTriangle, Printer, ScanLine, ShieldCheck } from "lucide-react";
import { authedFileUrl } from "@/lib/authedFileUrl";

/**
 * /documents — Member document vault.
 * - Upload passport, pilot license, medical certificate, driver's license, resume, training pro-card.
 * - Files are stored server-side; vision OCR (via Emergent LLM key) auto-extracts and maps to profile.
 * - Consolidated PDF export of all uploaded documents for insurance / employment sharing.
 */

const STATUS_META = {
  queued:     { icon: Loader2,       tone: "text-white/60",  spin: true,  label: "Queued" },
  processing: { icon: ScanLine,      tone: "text-cyan",      spin: false, label: "Scanning…" },
  done:       { icon: CheckCircle2,  tone: "text-avail",     spin: false, label: "Extracted" },
  error:      { icon: AlertTriangle, tone: "text-aog",       spin: false, label: "Failed" },
};

export default function Documents() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        api.get("/documents/types"),
        api.get("/documents/mine"),
      ]);
      setTypes(t.data.types || []);
      setDocs(d.data.documents || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any doc is still queued/processing
  useEffect(() => {
    const pending = docs.some((d) => d.ocr_status === "queued" || d.ocr_status === "processing");
    if (!pending) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [docs, load]);

  const onUpload = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.document.label} uploaded — scanning now.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  const onDelete = async (docId, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadConsolidated = async () => {
    try {
      const tok = localStorage.getItem("pjp_token");
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/documents/consolidated.pdf`;
      const res = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `PJP-Documents-${user?.last_name || ""}-${user?.first_name || ""}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Consolidated PDF downloaded");
    } catch {
      toast.error("PDF download failed");
    }
  };

  if (!user) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-white/60">Sign in to upload your documents.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="documents-page">
      <BackToCockpit />
      <SectionTitle
        overline="// Member Document Vault"
        title="Your credentials, extracted and verified."
        subtitle="Upload once — vision-OCR auto-fills your profile (name, DOB, license #, medical class, expiries). Data is encrypted and shared only with employers you approve."
      />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Badge tone="avail" pulse>Vision OCR</Badge>
        {user?.documents_lock_name ? (
          <span className="chip chip-avail inline-flex items-center gap-1.5" data-testid="name-verified-badge">
            <ShieldCheck className="h-3 w-3" /> Legal name verified from ID
          </span>
        ) : (
          <span className="chip chip-neutral inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Upload passport/DL to verify your legal name
          </span>
        )}
        <button
          onClick={downloadConsolidated}
          disabled={docs.length === 0}
          data-testid="documents-download-consolidated"
          className="btn btn-sm ml-auto"
        >
          <Printer className="h-3.5 w-3.5" /> Download consolidated PDF
        </button>
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {types.map((t) => {
            const uploaded = docs.filter((d) => d.doc_type === t.key);
            return (
              <DocumentCard
                key={t.key}
                type={t}
                uploaded={uploaded}
                uploading={uploadingType === t.key}
                onUpload={(f) => onUpload(t.key, f)}
                onDelete={onDelete}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function DocumentCard({ type, uploaded, uploading, onUpload, onDelete }) {
  const inputRef = useRef(null);

  return (
    <Card className="hud-corners relative" data-testid={`doc-card-${type.key}`}>
      <span className="hud-c-bl" /><span className="hud-c-br" />
      <div className="flex items-start gap-3 mb-4">
        <FileText className="h-5 w-5 text-sky mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-white">{type.label}</div>
          <div className="text-[11px] text-white/45 mt-0.5">
            {uploaded.length} file{uploaded.length === 1 ? "" : "s"} · OCR intent: {type.ocr_intent}
          </div>
        </div>
        {/* Top-right thumbnail of the most recently uploaded file. */}
        {uploaded.length > 0 && <DocThumbnail doc={uploaded[0]} testId={`doc-thumb-${type.key}`} />}
      </div>

      {/* Uploaded files list */}
      {uploaded.length > 0 && (
        <div className="space-y-2 mb-3">
          {uploaded.map((d) => <UploadedDoc key={d.id} d={d} onDelete={onDelete} />)}
        </div>
      )}

      {/* Upload button — accepts images AND PDFs. Resume/proof-of-hours are typically PDFs
          while passport/DL/medical are usually photos. Backend OCR pipeline handles both. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.heic,.heif,.webp"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
        data-testid={`doc-file-${type.key}`}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`doc-upload-${type.key}`}
        className="w-full btn btn-sm justify-center"
        style={{ background: uploading ? "#0d1a26" : "rgba(75,156,255,0.15)", borderColor: "rgba(75,156,255,0.4)", color: "#4B9CFF" }}
      >
        {uploading ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
        ) : (
          <><Upload className="h-3.5 w-3.5" /> {uploaded.length > 0 ? "Add another" : "Upload"}</>
        )}
      </button>

      {/* Security note — same for every doc card so members always see how their data is protected. */}
      <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-start gap-2" data-testid={`doc-security-note-${type.key}`}>
        <ShieldCheck className="h-3 w-3 mt-0.5 text-avail shrink-0" />
        <div className="text-[10px] text-white/45 leading-relaxed">
          <span className="text-avail mono tracking-wide">SECURED</span> · Encrypted in transit,
          owner-only access, no cache or CDN storage. Only you (and PJP admins performing
          verifications) can view this file.
        </div>
      </div>
    </Card>
  );
}

function UploadedDoc({ d, onDelete }) {
  const s = STATUS_META[d.ocr_status] || STATUS_META.queued;
  const Icon = s.icon;
  const [showFields, setShowFields] = useState(false);

  const fields = d.ocr_result && typeof d.ocr_result === "object" ? d.ocr_result : null;
  const fieldEntries = fields ? Object.entries(fields).filter(([k, v]) => v !== null && v !== "" && !k.startsWith("_")) : [];

  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid={`doc-row-${d.id}`}>
      <div className="flex items-center gap-2.5">
        <div className={`flex items-center gap-1.5 text-[11px] mono tracking-wider ${s.tone}`}>
          <Icon className={`h-3.5 w-3.5 ${s.spin ? "animate-spin" : ""}`} />
          <span className="uppercase">{s.label}</span>
        </div>
        <div className="text-[11px] text-white/55 truncate flex-1" title={d.filename}>
          {d.filename || "file"}
        </div>
        {d.url && (
          <a href={authedFileUrl(d.url)} target="_blank" rel="noopener noreferrer" className="text-[11px] text-sky hover:text-white mono">VIEW</a>
        )}
        <button
          onClick={() => onDelete(d.id, d.label)}
          data-testid={`doc-delete-${d.id}`}
          className="text-white/40 hover:text-aog p-1"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {d.ocr_status === "done" && fieldEntries.length > 0 && (
        <>
          <button
            onClick={() => setShowFields((v) => !v)}
            className="mt-2 mono text-[10px] tracking-widest text-cyan hover:text-white"
            data-testid={`doc-toggle-fields-${d.id}`}
          >
            {showFields ? "− HIDE" : "+ SHOW"} {fieldEntries.length} EXTRACTED FIELDS
          </button>
          {showFields && (
            <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]" data-testid={`doc-fields-${d.id}`}>
              {fieldEntries.map(([k, v]) => (
                <div key={k} className="border border-white/[0.05] rounded px-2 py-1">
                  <div className="mono text-[9px] text-white/40 tracking-wider">{k.toUpperCase()}</div>
                  <div className="text-white/85 truncate" title={String(v)}>{Array.isArray(v) ? v.join(", ") : String(v)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {d.ocr_status === "error" && d.ocr_error && (
        <div className="mt-2 text-[11px] text-aog/80">Error: {d.ocr_error}</div>
      )}
    </div>
  );
}


/**
 * DocThumbnail — small preview tile shown in the top-right of every DocumentCard.
 * Images render inline; PDFs show a small PDF icon badge with the filename so users
 * still see *something* representing the file they uploaded. Click opens a
 * full-viewport preview overlay (image zoom or embedded PDF iframe).
 */
function DocThumbnail({ doc, testId }) {
  const [open, setOpen] = useState(false);
  const isImage = (doc.content_type || "").startsWith("image/") || /\.(png|jpe?g|webp|heic|heif|gif)$/i.test(doc.filename || "");
  const isPdf = doc.content_type === "application/pdf" || /\.pdf$/i.test(doc.filename || "");
  const src = authedFileUrl(doc.url);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={testId}
        title="View document"
        className="h-14 w-14 shrink-0 rounded-lg overflow-hidden border border-white/15 bg-black/40 grid place-items-center hover:border-sky/60 transition-colors relative group"
      >
        {isImage && doc.url ? (
          <img src={src} alt={doc.filename || ""} className="h-full w-full object-cover" />
        ) : isPdf ? (
          <>
            <FileText className="h-5 w-5 text-orange-hud" />
            <div className="absolute bottom-0 left-0 right-0 bg-orange-hud/90 text-black text-[8px] mono font-bold text-center leading-tight py-0.5">PDF</div>
          </>
        ) : (
          <FileText className="h-5 w-5 text-white/50" />
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
          <span className="mono text-[9px] tracking-widest text-white">VIEW</span>
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center px-4 py-8"
          onClick={() => setOpen(false)}
          data-testid="doc-preview-overlay"
        >
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(false)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white mono text-xs tracking-widest"
              data-testid="doc-preview-close"
            >
              CLOSE ×
            </button>
            <div className="mono text-[10px] text-white/60 tracking-widest mb-2 truncate max-w-full">{doc.filename}</div>
            {isImage ? (
              <img src={src} alt={doc.filename || ""} className="max-w-full max-h-[80vh] rounded-lg border border-white/15 mx-auto object-contain" />
            ) : isPdf ? (
              <iframe
                src={src}
                title={doc.filename || "PDF"}
                className="w-full h-[80vh] rounded-lg border border-white/15 bg-white"
              />
            ) : (
              <div className="p-10 text-center bg-black/50 rounded-lg border border-white/15">
                <FileText className="h-10 w-10 text-white/40 mx-auto mb-3" />
                <div className="mono text-xs text-white/60">No inline preview available.</div>
                <a href={src} target="_blank" rel="noopener noreferrer" className="btn btn-primary mt-4 inline-flex">Open in new tab</a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
